import React, { useState, useEffect } from 'react';
import axios from 'axios';

const VendorSearchModal = ({ transaction, onClose, onVendorSelected }) => {
  const [vendors, setVendors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [addLookup, setAddLookup] = useState(true);
  const [lookupText, setLookupText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredVendors(vendors);
    } else {
      const filtered = vendors.filter(vendor =>
        vendor.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredVendors(filtered);
    }
  }, [searchTerm, vendors]);

  const fetchVendors = async () => {
    try {
      const response = await axios.get('/api/vendors?includeInactive=false&limit=1000');
      setVendors(response.data.vendors || []);
      setFilteredVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
      setError('Failed to load vendors');
    }
  };

  const handleVendorSelect = (vendor) => {
    setSelectedVendor(vendor);
    // Initialize lookup text with transaction description when vendor is selected
    if (transaction.description) {
      setLookupText(transaction.description.trim());
    }
  };

  const handleSubmit = async () => {
    if (!selectedVendor) {
      setError('Please select a vendor');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Update transaction with selected vendor
      await axios.put(`/api/statements/transactions/${transaction.id}`, {
        vendorId: selectedVendor.id
      });

      // If addLookup is checked, add the lookup text as a lookup
      if (addLookup && lookupText && lookupText.trim()) {
        try {
          await axios.post(`/api/vendors/${selectedVendor.id}/lookups`, {
            lookupText: lookupText.trim()
          });
        } catch (lookupError) {
          console.error('Failed to add lookup:', lookupError);
          // Don't fail the whole operation if lookup fails
        }
      }

      onVendorSelected(selectedVendor);
    } catch (error) {
      console.error('Error updating vendor:', error);
      setError(error.response?.data?.error || 'Failed to update vendor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Select Vendor</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            &times;
          </button>
        </div>

        {/* Transaction Info */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <p className="text-sm text-gray-600">Transaction Description:</p>
          <p className="text-sm font-medium text-gray-900">{transaction.description}</p>
          <p className="text-xs text-gray-500 mt-1">
            Amount: ${parseFloat(transaction.amount).toFixed(2)} | Date: {transaction.transactionDate}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Search Box */}
        <div className="px-6 py-4">
          <input
            type="text"
            placeholder="Search vendors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        </div>

        {/* Vendor List */}
        <div className="flex-1 overflow-y-auto px-6 pb-4">
          {filteredVendors.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              {searchTerm ? 'No vendors found matching your search' : 'No vendors available'}
            </p>
          ) : (
            <div className="space-y-2">
              {filteredVendors.map(vendor => (
                <div
                  key={vendor.id}
                  onClick={() => handleVendorSelect(vendor)}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedVendor?.id === vendor.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 pr-2">
                      <p className="font-medium text-gray-900">{vendor.name}</p>
                      {vendor.glAccount && (
                        <p className="text-sm text-gray-700 mt-1 break-words">
                          <span className="font-semibold">{vendor.glAccount.accountNumber}</span>
                          <span className="text-gray-600"> - {vendor.glAccount.accountName}</span>
                        </p>
                      )}
                      {vendor.lookups && vendor.lookups.length > 0 && (
                        <p className="text-xs text-gray-400 mt-1">
                          Lookups: {vendor.lookups.slice(0, 3).join(', ')}
                          {vendor.lookups.length > 3 && ` +${vendor.lookups.length - 3} more`}
                        </p>
                      )}
                    </div>
                    {selectedVendor?.id === vendor.id && (
                      <svg className="w-6 h-6 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Lookup Option */}
        {selectedVendor && transaction.description && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={addLookup}
                onChange={(e) => setAddLookup(e.target.checked)}
                className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">
                  Add automatic lookup for future matching
                </span>
                <p className="text-xs text-gray-600 mt-1 mb-2">
                  Add a lookup pattern for {selectedVendor.name} so similar transactions are automatically matched in the future.
                </p>
                {addLookup && (
                  <input
                    type="text"
                    value={lookupText}
                    onChange={(e) => setLookupText(e.target.value)}
                    placeholder="Enter lookup pattern..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </div>
            </label>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedVendor || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Applying...' : 'Apply Vendor'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorSearchModal;
